/**
 * Lightweight dependency injection container.
 *
 * Design goals:
 * - Zero dependencies, no decorators, no reflect-metadata
 * - Fully type-safe via branded token symbols
 * - Easy to swap implementations in tests
 *
 * Usage:
 * ```ts
 * // 1. Create typed tokens
 * const TOKENS = {
 *   Analytics: createToken<AnalyticsService>('Analytics'),
 * };
 *
 * // 2. Register implementations
 * const container = new Container()
 *   .provide(TOKENS.Analytics, new SentryAnalyticsService());
 *
 * // 3. Resolve in components via useInject()
 * const analytics = useInject(TOKENS.Analytics);
 * ```
 */

// Branded symbol type — ensures token type-safety without runtime overhead
export type Token<T> = symbol & { readonly __tokenType: T };

/**
 * Creates a typed DI token. Use one token per abstraction.
 */
export function createToken<T>(description: string): Token<T> {
  return Symbol(description) as Token<T>;
}

export class Container {
  private readonly registry = new Map<symbol, unknown>();

  /**
   * Registers a value for a token. Returns `this` for chaining.
   */
  provide<T>(token: Token<T>, value: T): this {
    this.registry.set(token, value);
    return this;
  }

  /**
   * Resolves a registered value by token.
   * Throws if no binding is found — fail-fast is intentional.
   */
  inject<T>(token: Token<T>): T {
    const value = this.registry.get(token);
    if (value === undefined) {
      throw new Error(
        `[DI] No provider found for token: "${String(token)}". Did you register it in the container?`,
      );
    }
    return value as T;
  }

  /**
   * Returns true if a token has been registered.
   */
  has<T>(token: Token<T>): boolean {
    return this.registry.has(token);
  }

  /**
   * Creates a child container that inherits all parent bindings.
   * Child overrides take precedence. Useful for test containers.
   */
  createChild(): Container {
    const child = new Container();
    this.registry.forEach((value, key) => {
      child.registry.set(key, value);
    });
    return child;
  }
}
